import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SectionLabel, CornerCard, StatusPip } from "./primitives";

/**
 * [01] Sample analysis. A real drone tile (faba bean field, early
 * leaf stage) annotated with the UltraScan AI report against it.
 * Reads as the actual product output: image-viewer chrome on top,
 * floating finding chips, a crop-id card + density readout + findings
 * list on the right, then a detailed grid of each finding with the
 * model's reasoning and the scout-team action.
 *
 * All copy is translated via the `sample.*` namespace; per-finding
 * title / summary / action live under `sample.items.<id>.*` and the
 * data array here just carries the id, severity, confidence + pip.
 */

type Pip = "coral" | "amber" | "saffron" | "water" | "violet" | "mute";

type Finding = {
  id: "stand" | "compaction" | "weeds" | "residue";
  kindKey: string;
  severity: 1 | 2 | 3 | 4 | 5;
  confidence: number;
  pip: Pip;
};

const FINDINGS: Finding[] = [
  { id: "stand",      kindKey: "missing_rows", severity: 4, confidence: 0.91, pip: "coral"   },
  { id: "compaction", kindKey: "other",        severity: 3, confidence: 0.87, pip: "amber"   },
  { id: "weeds",      kindKey: "weeds",        severity: 2, confidence: 0.79, pip: "saffron" },
  { id: "residue",    kindKey: "other",        severity: 1, confidence: 0.72, pip: "water"   },
];

export function SampleAnalysis() {
  const { t } = useTranslation();
  const density = { observed: 27, targetLow: 40, targetHigh: 50 };
  const deficit =
    1 - density.observed / ((density.targetLow + density.targetHigh) / 2);
  const deficitPct = Math.round(deficit * 100);

  return (
    <section className="relative border-b border-[color:var(--color-app-edge)] bg-[color:var(--color-app-card)]/30">
      <div className="absolute inset-0 bg-fieldgrid pointer-events-none opacity-50" />
      <div className="relative mx-auto max-w-[1320px] px-6 lg:px-10 py-24 md:py-28">
        <SectionLabel
          index={t("sample.section.index")}
          title={t("sample.section.title")}
          kicker={t("sample.section.kicker")}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <h2 className="text-display text-5xl md:text-6xl">
            {t("sample.headline.before")}{" "}
            <span className="text-accent text-accent-coral">
              {t("sample.headline.accent")}
            </span>
          </h2>
          <div className="md:pl-8 md:border-l md:border-[color:var(--color-app-edge)] text-[color:var(--color-app-text)]/75 leading-relaxed flex items-center">
            <p>{t("sample.intro")}</p>
          </div>
        </div>

        {/* ── Viewer + Summary side-by-side ─────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-6">
          <Viewer deficitPct={deficitPct} />
          <SummaryPanel
            density={density}
            deficitPct={deficitPct}
            findings={FINDINGS}
          />
        </div>

        {/* ── Detailed findings ─────────────────────────────── */}
        <div className="mt-12">
          <div className="flex items-baseline justify-between mb-5">
            <h3 className="text-display text-3xl">
              {t("sample.details.title")}
            </h3>
            <div className="coord-mono opacity-60">
              {t("sample.details.expanded", {
                count: FINDINGS.length,
                total: FINDINGS.length,
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {FINDINGS.map((f, i) => (
              <FindingCard key={f.id} finding={f} index={i} />
            ))}
          </div>
        </div>

        {/* ── Recommended actions ───────────────────────────── */}
        <div className="mt-12">
          <CornerCard className="p-7 md:p-10" tint="amber">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-8">
              <div>
                <div className="label-mono opacity-60 mb-2">
                  {t("sample.actions.header")}
                </div>
                <h3 className="text-display text-3xl md:text-4xl">
                  {t("sample.actions.headline.before")}{" "}
                  <span className="text-accent text-accent-amber">
                    {t("sample.actions.headline.accent")}
                  </span>
                  {t("sample.actions.headline.after")}
                </h3>
                <p className="mt-4 text-sm text-[color:var(--color-app-text)]/70 leading-relaxed">
                  {t("sample.actions.intro")}
                </p>
              </div>
              <ol className="space-y-4 lg:pl-8 lg:border-l lg:border-[color:var(--color-app-edge)]">
                {(["step1", "step2", "step3"] as const).map((step, i) => (
                  <ActionItem
                    key={step}
                    n={String(i + 1).padStart(2, "0")}
                    due={t(`sample.actions.${step}.due`)}
                    body={t(`sample.actions.${step}.body`)}
                  />
                ))}
              </ol>
            </div>
          </CornerCard>
        </div>
      </div>
    </section>
  );
}

/* ── Viewer ─────────────────────────────────────────────────────── */

function Viewer({ deficitPct }: { deficitPct: number }) {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <div className="absolute -top-3 -left-3 coord-mono opacity-60">+</div>
      <div className="absolute -top-3 right-0 coord-mono opacity-60">+</div>

      <div className="relative border border-[color:var(--color-app-edge-2)] bg-[color:var(--color-app-bg)]">
        {/* Top chrome */}
        <div className="flex items-center justify-between gap-4 px-3 py-2 border-b border-[color:var(--color-app-edge)] bg-[color:var(--color-app-bg)]/80">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[color:var(--color-amber)] pulse-dot" />
            <span className="label-mono">{t("sample.viewer.tile")}</span>
          </div>
          <div className="hidden sm:flex items-center gap-3 coord-mono opacity-70">
            <span>{t("sample.viewer.gsd")}</span>
            <span>·</span>
            <span>{t("sample.viewer.anomalies")}</span>
          </div>
        </div>

        {/* Image canvas */}
        <div className="relative aspect-[16/9] overflow-hidden bg-[#1e2010]">
          <img
            src={`${import.meta.env.BASE_URL}sample-scan.jpg`}
            alt={t("sample.viewer.tile")}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />

          {/* Annotation overlay */}
          <svg
            viewBox="0 0 1600 900"
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full pointer-events-none"
          >
            {/* Wheel compaction — two narrow vertical tracks hugging
                the left edge of the tile where the sprayer wheels
                chewed up the rows. */}
            <polygon
              points="0,0 55,0 60,900 0,900"
              fill="rgba(217, 119, 6, 0.22)"
              stroke="rgba(251, 191, 36, 0.95)"
              strokeWidth={2.5}
              strokeDasharray="6 4"
            />
            <polygon
              points="85,0 140,0 148,900 90,900"
              fill="rgba(217, 119, 6, 0.22)"
              stroke="rgba(251, 191, 36, 0.95)"
              strokeWidth={2.5}
              strokeDasharray="6 4"
            />

            {/* Uneven stand — two gap zones away from the tracks. */}
            <polygon
              points="540,160 1080,140 1120,420 580,440"
              fill="rgba(208, 92, 117, 0.20)"
              stroke="rgba(239, 138, 161, 0.95)"
              strokeWidth={2.5}
            />
            <polygon
              points="960,600 1500,580 1530,860 980,870"
              fill="rgba(208, 92, 117, 0.20)"
              stroke="rgba(239, 138, 161, 0.95)"
              strokeWidth={2.5}
            />

            {/* Weed pressure — small cluster near top centre. */}
            <polygon
              points="690,10 830,10 840,90 700,95"
              fill="rgba(199, 154, 47, 0.30)"
              stroke="rgba(236, 195, 106, 1)"
              strokeWidth={2.5}
            />

            {/* Maize-residue markers — kept clear of the tracks. */}
            <g stroke="rgba(95, 179, 196, 0.85)" strokeWidth={1.6} fill="none">
              {[
                [520, 720],
                [820, 230],
                [1280, 380],
              ].map(([x, y], i) => (
                <g key={i}>
                  <circle cx={x} cy={y} r={14} />
                  <line x1={x - 8} y1={y} x2={x + 8} y2={y} />
                  <line x1={x} y1={y - 8} x2={x} y2={y + 8} />
                </g>
              ))}
            </g>
            <g stroke="rgba(255, 255, 255, 0.95)" strokeWidth={1.5}>
              <line x1={1240} y1={730} x2={1264} y2={730} />
              <line x1={1238} y1={730} x2={1214} y2={730} />
              <line x1={1240} y1={730} x2={1240} y2={754} />
              <line x1={1240} y1={730} x2={1240} y2={706} />
            </g>
          </svg>

          {/* Floating annotation chips */}
          <ChipPos x="11%" y="50%" anchor="start">
            <StatusPip color="amber" pulse>{t("sample.viewer.chipCompaction")}</StatusPip>
          </ChipPos>
          <ChipPos x="52%" y="30%" anchor="center">
            <StatusPip color="coral" pulse>{t("sample.viewer.chipStand")}</StatusPip>
          </ChipPos>
          <ChipPos x="50%" y="3%" anchor="center">
            <StatusPip color="saffron" pulse>{t("sample.viewer.chipWeeds")}</StatusPip>
          </ChipPos>
          <ChipPos x="80%" y="83%" anchor="end">
            <StatusPip color="coral" pulse>
              {t("sample.viewer.chipDeficit", { percent: deficitPct })}
            </StatusPip>
          </ChipPos>
        </div>

        {/* Bottom chrome */}
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-[color:var(--color-app-edge)] bg-[color:var(--color-app-bg)]/80 coord-mono">
          <span className="opacity-70">{t("sample.viewer.confidence")}</span>
          <span className="hidden sm:inline opacity-70">
            {t("sample.viewer.vision")}
          </span>
          <span className="opacity-70">{t("sample.viewer.findings")}</span>
        </div>
      </div>
    </div>
  );
}

function ChipPos({
  x,
  y,
  anchor,
  children,
}: {
  x: string;
  y: string;
  anchor: "start" | "center" | "end";
  children: ReactNode;
}) {
  const tf =
    anchor === "center" ? "translate(-50%, 0)" :
    anchor === "end"    ? "translate(-100%, 0)" :
                           "translate(0, 0)";
  return (
    <div
      className="absolute px-2 py-1 bg-[color:var(--color-app-bg)]/90 border border-[color:var(--color-app-edge-2)] backdrop-blur-sm"
      style={{ left: x, top: y, transform: tf }}
    >
      {children}
    </div>
  );
}

/* ── Summary panel ──────────────────────────────────────────────── */

function SummaryPanel({
  density,
  deficitPct,
  findings,
}: {
  density: { observed: number; targetLow: number; targetHigh: number };
  deficitPct: number;
  findings: Finding[];
}) {
  const { t } = useTranslation();
  const cropConfidence = 0.94;
  return (
    <div className="grid grid-cols-1 gap-4">
      {/* Crop ID card */}
      <div className="relative border border-[color:var(--color-app-edge-2)] bg-[color:var(--color-app-card)] p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="label-mono opacity-70">{t("sample.crop.label")}</div>
          <span className="coord-mono opacity-60">
            {t("sample.crop.confLabel", { value: cropConfidence.toFixed(2) })}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <LeafGlyph />
          <div className="min-w-0">
            <div className="text-display text-3xl leading-none">
              {t("sample.crop.name")}
            </div>
            <div className="coord-mono opacity-70 mt-1.5">
              {t("sample.crop.latin")} · {t("sample.crop.stage")}
            </div>
          </div>
        </div>
      </div>

      {/* Density readout */}
      <div className="relative border border-[color:var(--color-app-edge-2)] bg-[color:var(--color-app-card)] p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div className="label-mono opacity-70">{t("sample.density.label")}</div>
          <div className="coord-mono text-[color:var(--color-coral)] num">
            {t("sample.density.deficit", { percent: deficitPct })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-3">
          <Readout
            label={t("sample.density.observed")}
            value={t("sample.density.observedValue", { count: density.observed })}
            accent="coral"
          />
          <Readout
            label={t("sample.density.target")}
            value={t("sample.density.targetValue", {
              low: density.targetLow,
              high: density.targetHigh,
            })}
          />
        </div>
        <div className="relative h-2 bg-[color:var(--color-app-edge)]">
          <div
            className="absolute top-0 bottom-0 bg-[color:var(--color-leaf)]/30"
            style={{ left: "60%", right: "10%" }}
          />
          <div
            className="absolute top-[-3px] bottom-[-3px] w-[3px] bg-[color:var(--color-coral)]"
            style={{ left: "40%" }}
          />
        </div>
        <div className="mt-1.5 flex justify-between coord-mono opacity-50">
          <span>0</span>
          <span>25</span>
          <span>50</span>
          <span>75</span>
        </div>
      </div>

      {/* Findings list */}
      <div className="relative border border-[color:var(--color-app-edge-2)] bg-[color:var(--color-app-card)]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[color:var(--color-app-edge)]">
          <div className="label-mono opacity-70">{t("sample.findings.label")}</div>
          <div className="coord-mono opacity-60 num">
            {t("sample.findings.total", { count: findings.length })}
          </div>
        </div>
        <ul className="divide-y divide-[color:var(--color-app-edge)]">
          {findings.map((f) => (
            <li key={f.id} className="px-5 py-3 flex items-center gap-3">
              <div className="shrink-0 coord-mono num text-[color:var(--color-app-text)]">
                S{f.severity}
              </div>
              <div className="shrink-0 coord-mono opacity-60 num">
                {f.confidence.toFixed(2)}
              </div>
              <div className="flex-1 min-w-0 text-sm truncate">
                {t(`sample.items.${f.id}.title`)}
              </div>
              <span className={`w-1.5 h-1.5 rounded-full ${pipBg(f.pip)}`} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Readout({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: "coral";
}) {
  return (
    <div>
      <div className="label-mono opacity-60 mb-1">{label}</div>
      <div
        className={`text-display text-2xl num ${
          accent === "coral" ? "text-[color:var(--color-coral)]" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function pipBg(pip: Pip) {
  return {
    coral:   "bg-[color:var(--color-coral)]",
    amber:   "bg-[color:var(--color-amber)]",
    saffron: "bg-[color:var(--color-saffron)]",
    water:   "bg-[color:var(--color-water)]",
    violet:  "bg-[color:var(--color-violet)]",
    mute:    "bg-[color:var(--color-app-mute)]",
  }[pip];
}

/* ── Detailed finding card ──────────────────────────────────────── */

function FindingCard({ finding, index }: { finding: Finding; index: number }) {
  const { t } = useTranslation();
  return (
    <CornerCard tint={finding.pip === "mute" || finding.pip === "violet" ? undefined : finding.pip}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-[color:var(--color-app-edge)]">
        <span className="label-mono opacity-70">
          <span className="bracket">{String(index + 1).padStart(2, "0")}</span>{" "}
          {t(`sample.kinds.${finding.kindKey}`)}
        </span>
        <div className="flex items-center gap-3 coord-mono">
          <span className="opacity-70 num">
            {t("sample.details.severityFull", { n: finding.severity })}
          </span>
          <span className="opacity-50">·</span>
          <span className="opacity-70 num">{finding.confidence.toFixed(2)}</span>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h4 className="text-display text-2xl leading-tight">
            {t(`sample.items.${finding.id}.title`)}
          </h4>
          <StatusPip color={finding.pip} pulse>
            {t("sample.details.severityChip", { n: finding.severity })}
          </StatusPip>
        </div>
        <p className="text-sm text-[color:var(--color-app-text)]/80 leading-relaxed mb-5">
          {t(`sample.items.${finding.id}.summary`)}
        </p>
        <div className="border-t border-[color:var(--color-app-edge)] pt-4">
          <div className="label-mono opacity-60 mb-2">
            {t("sample.details.actionLabel")}
          </div>
          <p className="text-sm text-[color:var(--color-app-text)]/85 leading-relaxed">
            {t(`sample.items.${finding.id}.action`)}
          </p>
        </div>
      </div>
    </CornerCard>
  );
}

function ActionItem({
  n,
  due,
  body,
}: {
  n: string;
  due: string;
  body: string;
}) {
  return (
    <li className="grid grid-cols-[40px_1fr] gap-4">
      <div>
        <div className="label-mono">
          <span className="bracket">{n}</span>
        </div>
      </div>
      <div>
        <div className="coord-mono opacity-70 mb-1.5">DUE · {due}</div>
        <div className="text-sm text-[color:var(--color-app-text)]/85 leading-relaxed">
          {body}
        </div>
      </div>
    </li>
  );
}

/* ── Inline leaf glyph for the crop ID card ─────────────────────── */

function LeafGlyph() {
  return (
    <svg
      viewBox="0 0 56 56"
      className="w-12 h-12 shrink-0 text-[color:var(--color-leaf)]"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
    >
      <path d="M28 6 C18 16 14 28 16 38 C18 46 24 50 28 50 C32 50 38 46 40 38 C42 28 38 16 28 6 Z" />
      <line x1={28} y1={10} x2={28} y2={50} />
      <path d="M28 18 L22 24 M28 24 L20 30 M28 30 L21 36 M28 18 L34 24 M28 24 L36 30 M28 30 L35 36" />
    </svg>
  );
}
